import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Upload, Boxes, AlertTriangle, ChevronRight, ChevronDown } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { useTableSort, SortableTh } from "../../components/tableSort";
import WmsImportDialog from "../../components/WmsImportDialog";
import WmsPartitaDialog from "../../components/WmsPartitaDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtKg = (n) => (n ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");

const KpiCard = ({ label, value, sub, accent = "text-stone-900", testId }) => (
  <Card className="p-4 bg-white border-stone-200" data-testid={testId}>
    <p className={`text-xl lg:text-2xl font-bold ${accent}`}>{value}</p>
    <p className="text-xs text-stone-500 mt-0.5">{label}</p>
    {sub && <p className="text-[10px] text-stone-400">{sub}</p>}
  </Card>
);

/* Sotto-riga lotto/partita dentro un articolo espanso */
const LotSubRow = ({ p, onOpen }) => (
  <tr className="border-b border-stone-100 bg-stone-50/70 hover:bg-stone-100 cursor-pointer" onClick={() => onOpen(p.partita)} data-testid={`wms-lot-${p.partita}`}>
    <td className="p-2 pl-10 font-mono text-xs text-[#8A1C2A] whitespace-nowrap">
      {p.partita}
      {p.orphan && <AlertTriangle className="w-3 h-3 text-amber-500 inline ml-1" />}
    </td>
    <td className="p-2 font-mono text-xs">{p.lot || "—"}</td>
    <td className="p-2 text-xs whitespace-nowrap">{fmtDate(p.expiry_date)}</td>
    <td className="p-2 text-xs truncate max-w-[160px]">{p.origin === "supplier" ? (p.supplier_name || "Fornitore") : "Produzione"}</td>
    <td className="p-2 text-right text-emerald-700 text-xs">{fmtKg(p.qty_in)}</td>
    <td className="p-2 text-right text-red-700 text-xs">{fmtKg(p.qty_out)}</td>
    <td className={`p-2 text-right font-semibold text-xs ${p.balance > 0.001 ? "text-emerald-700" : "text-stone-400"}`}>{fmtKg(p.balance)}</td>
  </tr>
);

function GroupedTable({ loading, groups, sort, expanded, onToggle, onOpen }) {
  return (
    <Card className="bg-white border-stone-200 overflow-hidden">
      <div className="overflow-x-auto max-h-[62vh] overflow-y-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10">
            <tr>
              <SortableTh label="Articolo" sortId="product_code" sort={sort} />
              <SortableTh label="Lotti" sortId="lots_count" sort={sort} align="right" />
              <SortableTh label="Prossima scadenza" sortId="next_expiry" sort={sort} />
              <SortableTh label="Origine" sortId="supplier_name" sort={sort} />
              <SortableTh label="Kg caricati" sortId="qty_in" sort={sort} align="right" />
              <SortableTh label="Kg scaricati" sortId="qty_out" sort={sort} align="right" />
              <SortableTh label="Giacenza" sortId="balance" sort={sort} align="right" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="p-8 text-center text-stone-500">Caricamento…</td></tr>
            ) : groups.length === 0 ? (
              <tr><td colSpan="7" className="p-8 text-center text-stone-500" data-testid="wms-empty">Nessun articolo — carica l'export «Tracciabilità» da Etifood</td></tr>
            ) : groups.map((g) => (
              <React.Fragment key={g.product_code}>
                <tr className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer" onClick={() => onToggle(g.product_code)} data-testid={`wms-group-${g.product_code}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-1.5">
                      {expanded[g.product_code] ? <ChevronDown className="w-4 h-4 text-stone-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />}
                      <div className="min-w-0">
                        <p className="font-semibold truncate max-w-[320px]">{g.description || g.product_code}</p>
                        <p className="font-mono text-[11px] text-stone-500">{g.product_code}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <Badge className="bg-stone-100 text-stone-700 hover:bg-stone-100" data-testid={`wms-group-count-${g.product_code}`}>{g.lots_count}</Badge>
                  </td>
                  <td className="p-3 text-xs whitespace-nowrap">{fmtDate(g.next_expiry)}</td>
                  <td className="p-3 text-xs truncate max-w-[150px]">{g.supplier_name || (g.has_production ? "Produzione" : "—")}</td>
                  <td className="p-3 text-right text-emerald-700">{fmtKg(g.qty_in)}</td>
                  <td className="p-3 text-right text-red-700">{fmtKg(g.qty_out)}</td>
                  <td className={`p-3 text-right font-bold ${g.balance > 0.001 ? "text-emerald-700" : "text-stone-400"}`}>{fmtKg(g.balance)}</td>
                </tr>
                {expanded[g.product_code] && (
                  <tr className="bg-stone-50/70">
                    <td colSpan="7" className="px-3 pt-1 pb-0">
                      <p className="text-[10px] uppercase tracking-wide text-stone-400 pl-7">Partita · Lotto · Scadenza · Origine · Kg + / Kg − / Giacenza</p>
                    </td>
                  </tr>
                )}
                {expanded[g.product_code] && g.lots.map((p) => <LotSubRow key={p.partita} p={p} onOpen={onOpen} />)}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function FlatTable({ loading, rows, sort, onOpen }) {
  return (
    <Card className="bg-white border-stone-200 overflow-hidden">
      <div className="overflow-x-auto max-h-[62vh] overflow-y-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10">
            <tr>
              <SortableTh label="Partita" sortId="partita" sort={sort} />
              <SortableTh label="Articolo" sortId="product_code" sort={sort} />
              <SortableTh label="Descrizione" sortId="description" sort={sort} />
              <SortableTh label="Lotto" sortId="lot" sort={sort} />
              <SortableTh label="Scadenza" sortId="expiry_date" sort={sort} />
              <SortableTh label="Origine" sortId="supplier_name" sort={sort} />
              <SortableTh label="Kg caricati" sortId="qty_in" sort={sort} align="right" />
              <SortableTh label="Kg scaricati" sortId="qty_out" sort={sort} align="right" />
              <SortableTh label="Giacenza" sortId="balance" sort={sort} align="right" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="p-8 text-center text-stone-500">Caricamento…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="9" className="p-8 text-center text-stone-500" data-testid="wms-empty">Nessuna partita — carica l'export «Tracciabilità» da Etifood</td></tr>
            ) : rows.map((p) => (
              <tr key={p.partita} className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer" onClick={() => onOpen(p.partita)} data-testid={`wms-row-${p.partita}`}>
                <td className="p-3 font-mono text-xs text-[#8A1C2A] font-semibold whitespace-nowrap">
                  {p.partita}
                  {p.orphan && <AlertTriangle className="w-3 h-3 text-amber-500 inline ml-1" />}
                </td>
                <td className="p-3 font-mono text-xs">{p.product_code}</td>
                <td className="p-3 truncate max-w-[240px]">{p.description}</td>
                <td className="p-3 font-mono text-xs">{p.lot || "—"}</td>
                <td className="p-3 text-xs whitespace-nowrap">{fmtDate(p.expiry_date)}</td>
                <td className="p-3 text-xs truncate max-w-[140px]">{p.origin === "supplier" ? (p.supplier_name || "Fornitore") : "Produzione"}</td>
                <td className="p-3 text-right text-emerald-700">{fmtKg(p.qty_in)}</td>
                <td className="p-3 text-right text-red-700">{fmtKg(p.qty_out)}</td>
                <td className={`p-3 text-right font-bold ${p.balance > 0.001 ? "text-emerald-700" : "text-stone-400"}`}>{fmtKg(p.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function WmsGiacenze() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [viewMode, setViewMode] = useState("article"); // article | lot
  const [expanded, setExpanded] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const sort = useTableSort();

  useEffect(() => {
    axios.get(`${API}/wms/summary`).then((r) => setSummary(r.data)).catch(() => {});
  }, [reloadKey]);

  const load = useCallback(async () => {
    try {
      const params = { only_available: onlyAvailable };
      if (search) params.search = search;
      const r = await axios.get(`${API}/wms/partite`, { params });
      setRows(r.data || []);
    } catch { toast.error("Errore caricamento giacenze"); }
    finally { setLoading(false); }
  }, [search, onlyAvailable]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load, reloadKey]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const p of rows) {
      const key = p.product_code || "—";
      if (!map.has(key)) {
        map.set(key, { product_code: key, description: "", lots: [], qty_in: 0, qty_out: 0, balance: 0, next_expiry: null, supplier_name: "", has_production: false });
      }
      const g = map.get(key);
      g.lots.push(p);
      g.qty_in += p.qty_in;
      g.qty_out += p.qty_out;
      g.balance += p.balance;
      if (!g.description && p.description) g.description = p.description;
      if (p.balance > 0.001 && p.expiry_date && (!g.next_expiry || p.expiry_date < g.next_expiry)) g.next_expiry = p.expiry_date;
      if (!g.supplier_name && p.origin === "supplier" && p.supplier_name) g.supplier_name = p.supplier_name;
      if (p.origin !== "supplier") g.has_production = true;
    }
    return [...map.values()].map((g) => ({
      ...g,
      lots_count: g.lots.length,
      qty_in: Math.round(g.qty_in * 100) / 100,
      qty_out: Math.round(g.qty_out * 100) / 100,
      balance: Math.round(g.balance * 1000) / 1000,
    }));
  }, [rows]);

  const sortedGroups = sort.sortList(groups);
  const sortedRows = sort.sortList(rows);
  const toggleGroup = (code) => setExpanded((e) => ({ ...e, [code]: !e[code] }));

  return (
    <div className="space-y-4" data-testid="wms-giacenze-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2"><Boxes className="w-6 h-6 text-[#8A1C2A]" />WMS — Giacenze & Tracciabilità</h1>
          <p className="text-sm text-stone-500">
            Giacenze per partita sincronizzate dall'export Tracciabilità di Etifood
            {summary?.last_movement_date && <> · ultimo movimento: <b>{fmtDate(summary.last_movement_date)}</b></>}
          </p>
        </div>
        <Button onClick={() => setImportOpen(true)} className="bg-[#8A1C2A] hover:bg-[#701622]" data-testid="wms-import-btn">
          <Upload className="w-4 h-4 mr-1" />Importa Tracciabilità
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard label="Kg disponibili" value={fmtKg(summary.kg_available)} accent="text-emerald-700" testId="wms-kpi-available" />
          <KpiCard label="Partite con giacenza" value={summary.partite_available} sub={`${summary.partite_total} totali`} testId="wms-kpi-partite" />
          <KpiCard label="Kg venduti" value={fmtKg(summary.kg_sold)} accent="text-blue-700" testId="wms-kpi-sold" />
          <KpiCard label="Kg scarti lavorazione" value={fmtKg(summary.kg_waste)} accent="text-red-700" testId="wms-kpi-waste" />
        </div>
      )}

      {summary?.orphan_count > 0 && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid="wms-orphan-hint">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {summary.orphan_count} partite hanno scarichi ma il carico non è ancora stato importato (export da un periodo precedente): importa un export che parte da più indietro per completare le giacenze.
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center flex-wrap">
        <div className="flex rounded-lg border border-stone-300 overflow-hidden bg-white" data-testid="wms-view-toggle">
          <button
            className={`px-3 py-2 text-xs font-semibold transition-colors ${viewMode === "article" ? "bg-[#8A1C2A] text-white" : "bg-white text-stone-600 hover:bg-stone-50"}`}
            onClick={() => setViewMode("article")}
            data-testid="wms-view-article"
          >
            Per articolo
          </button>
          <button
            className={`px-3 py-2 text-xs font-semibold transition-colors ${viewMode === "lot" ? "bg-[#8A1C2A] text-white" : "bg-white text-stone-600 hover:bg-stone-50"}`}
            onClick={() => setViewMode("lot")}
            data-testid="wms-view-lot"
          >
            Per partita
          </button>
        </div>
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca partita, articolo, lotto, fornitore…" className="pl-9 bg-white" data-testid="wms-search" />
        </div>
        <label className="flex items-center gap-2 text-sm text-stone-600 whitespace-nowrap px-1">
          <input type="checkbox" checked={onlyAvailable} onChange={(e) => setOnlyAvailable(e.target.checked)} className="accent-[#8A1C2A]" data-testid="wms-only-available" />
          Solo con giacenza
        </label>
        {viewMode === "article" && !loading && (
          <span className="text-xs text-stone-400" data-testid="wms-group-summary">{groups.length} articoli · {rows.length} partite</span>
        )}
      </div>

      {viewMode === "article"
        ? <GroupedTable loading={loading} groups={sortedGroups} sort={sort} expanded={expanded} onToggle={toggleGroup} onOpen={setDetail} />
        : <FlatTable loading={loading} rows={sortedRows} sort={sort} onOpen={setDetail} />}

      {detail && <WmsPartitaDialog partita={detail} onClose={() => setDetail(null)} />}
      <WmsImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={() => setReloadKey((k) => k + 1)} />
    </div>
  );
}
